import { UserMessage, AIMessage, ReportSuggestionMessage, PBITableRow } from "./types";

export function createUserMessage(
  text: string,
  isAutoGenerated = false
): UserMessage {
  return {
    text,
    sender: "user",
    isAutoGenerated,
  };
}

function parseStructuredResponse(jsonString: string): {
  text: string;
  daxQuery?: string;
  pbiTable?: PBITableRow[];
} {
  try {
    // Sanitize NaN values which are invalid in JSON
    const sanitizedJson = jsonString.replace(/:\s*NaN/g, ': null');
    const parsed = JSON.parse(sanitizedJson);

    // Check if this is a structured response (must have "User Response")
    if ("User Response" in parsed) {
      const result: { text: string; daxQuery?: string; pbiTable?: PBITableRow[] } = {
        text: parsed["User Response"],
      };

      if (parsed["Dax Query"]) {
        result.daxQuery = parsed["Dax Query"];
      }
      if (parsed["PBI table"]) {
        result.pbiTable = parsed["PBI table"];
      }

      return result;
    }

    return { text: jsonString };
  } catch (error) {
    console.error("❌ Failed to parse response:", error);
    console.error("Raw response:", jsonString);
    return { text: jsonString };
  }
}

function extractResponseText(text: string | { response_text: string }): string {
  if (typeof text === "string") {
    return text;
  } else if (text?.response_text) {
    return text.response_text;
  } else {
    return JSON.stringify(text);
  }
}

export function createAIMessage(
  text: string | { response_text: string },
  messageId: number | null,
  isError = false
): AIMessage[] {
  const responseText = extractResponseText(text);
  const { text: finalText, daxQuery, pbiTable } = parseStructuredResponse(responseText);

  // If we have structured data, return two separate messages
  if (daxQuery || pbiTable) {
    return [
      // First message: Data and Query
      {
        text: "", // No text for data-only message
        sender: "ai",
        messageId: null, // No messageId for the data card
        isError: false,
        isReport: false,
        daxQuery,
        pbiTable,
      },
      // Second message: AI Response
      {
        text: finalText,
        sender: "ai",
        messageId,
        isError,
        isReport: false,
      },
    ];
  }

  // No structured data, return single message in array
  return [
    {
      text: finalText,
      sender: "ai",
      messageId,
      isError,
      isReport: false,
    },
  ];
}

export function createReportSuggestionMessage(
  text: string,
  messageId: number | null,
  reports: { title: string; url: string }[] = [],
  isError = false
): ReportSuggestionMessage {
  return {
    text,
    sender: "ai",
    messageId,
    isError,
    isReport: true,
    reports,
  };
}

/**
 * Preprocesses markdown text for consistent rendering
 * Normalizes line breaks, handles bold headings, and cleans up list formatting
 */
export function preprocessMarkdown(text: string): string {
  let normalized = text.replace(/\r\n/g, "\n");

  // Convert tabs to spaces (4 spaces per tab)
  normalized = normalized.replace(/\t/g, "    ");

  // Convert double newlines to markdown paragraph breaks (ensure they're preserved)
  // First, protect existing \n\n by temporarily marking them
  normalized = normalized.replace(/\n\n/g, "\n\n");

  // Only add newlines after bold headings if they're NOT in a list item
  // Match bold with colon NOT preceded by a dash (list marker)
  normalized = normalized.replace(/^(\*\*[^*]+\*\*:)\n?(?!\n)/gm, "$1\n\n");
  normalized = normalized.replace(/\n{3,}/g, "\n\n");
  normalized = normalized.replace(/([^\n])\s*-\s+/g, "$1\n- ");
  normalized = normalized.replace(/-\s+([^\n-][^\n]*?)\s+-\s+/g, "- $1\n- ");
  // If a bold heading appears mid-line after a list item, ensure it starts a new paragraph.
  normalized = normalized.replace(/(- .*?)\s+(\*\*[^*]+\*\*:)/g, "$1\n\n$2\n\n");
  return normalized;
}
